import { JSX, useMemo } from 'react'
import { Badge, Button, Modal, Table } from 'react-bootstrap'
import { t } from '../locale'
import { isRecord } from '../helpers/valueGuards'

interface KycChecksProps {
  show: boolean
  kyc?: unknown
  onClose?: () => void
  onDelete?: (kycNumber: number) => void
}

type AnyRecord = Record<string, unknown>

const SKIP_EINTRAG = new Set(['deleted', 'invalid', 'Nummer'])
const SKIP_GENERIC = new Set(['deleted', 'invalid'])
const RELATIONSHIP_KEYS = new Set(['Verwandtschaft'])

function boolStr(value: string): string {
  if (value === 'true') return 'Ja'
  if (value === 'false') return 'Nein'
  return value
}

function hitTypeVariant(hitType: string): string {
  const n = (hitType ?? '').toUpperCase()
  if (n.includes('SAN')) return 'danger'
  if (n.includes('PEP')) return 'warning'
  if (n.includes('WATCH')) return 'dark'
  return 'secondary'
}

function renderNestedAttributes(attrs: AnyRecord): JSX.Element[] {
  return Object.entries(attrs)
    .filter(([key, value]) => {
      if (key === 'datasets') return false
      if (value === null || value === undefined || value === '') return false
      if (typeof value === 'string' && value.toUpperCase() === 'UNKNOWN') return false
      return true
    })
    .map(([key, value]): JSX.Element | null => {
      if (Array.isArray(value)) {
        if (value.length === 0) return null
        const allPrimitive = value.every(v => !isRecord(v) && !Array.isArray(v))
        if (allPrimitive) {
          return (
            <div key={key} className='small'>
              <b>{key}</b>: {value.join(', ')}
            </div>
          )
        }
        return (
          <details key={key} className='small mb-1'>
            <summary style={{ cursor: 'pointer' }}>
              <b>{key}</b> <span className='text-muted'>({value.length})</span>
            </summary>
            <div className='ps-3 mt-1'>
              {value.map((v, i) => (
                <div key={i} className='border-start ps-2 mt-1 mb-1'>
                  {isRecord(v) ? renderNestedAttributes(v) : String(v)}
                </div>
              ))}
            </div>
          </details>
        )
      }
      if (isRecord(value)) return null
      const text = typeof value === 'boolean' ? (value ? 'Ja' : 'Nein') : String(value)
      return (
        <div key={key} className='small'>
          <b>{key}</b>: {text}
        </div>
      )
    })
    .filter((el): el is JSX.Element => el !== null)
}

function KycHit({ item, onDelete }: { item: AnyRecord; onDelete?: (n: number) => void }) {
  if (item.deleted || item.invalid) return null

  // Type 1: SCHUFA — has Eintrag
  if ('Eintrag' in item) {
    const eintrag = isRecord(item.Eintrag) ? item.Eintrag : {}
    const nummer = typeof eintrag.Nummer === 'number' ? eintrag.Nummer : undefined

    return (
      <div className='mb-3 pb-2 border-bottom'>
        <div className='mb-1 d-flex align-items-center gap-2'>
          {!!item.Liste && <strong>{String(item.Liste)}</strong>}
          {!!item.HitType && (
            <Badge bg={hitTypeVariant(String(item.HitType))}>{String(item.HitType)}</Badge>
          )}
          {nummer !== undefined && <span className='text-muted small'>#{nummer}</span>}
        </div>

        {Object.entries(eintrag).map(([key, value]) => {
          if (SKIP_EINTRAG.has(key) || value === null || value === undefined || value === '')
            return null

          if (RELATIONSHIP_KEYS.has(key)) {
            const rels = Array.isArray(value) ? value : isRecord(value) ? Object.values(value) : []
            return (
              <div key={key} className='small'>
                <b>{key}</b>:
                {(rels as AnyRecord[]).map((rel, i) => (
                  <div key={i} className='ps-3'>
                    {Object.entries(rel || {})
                      .filter(([, v]) => v)
                      .map(([rk, rv]) => (
                        <span key={rk} className='me-2'>
                          {rk}: {String(rv)}
                        </span>
                      ))}
                  </div>
                ))}
              </div>
            )
          }

          if (Array.isArray(value)) {
            const text = (value as unknown[]).filter(v => v != null && v !== '').join(', ')
            return text ? (
              <div key={key} className='small'>
                <b>{key}</b>: {text}
              </div>
            ) : null
          }

          if (isRecord(value)) return null
          return (
            <div key={key} className='small'>
              <b>{key}</b>: {String(value)}
            </div>
          )
        })}

        {nummer !== undefined && onDelete && (
          <Button
            variant='outline-danger'
            size='sm'
            className='mt-2'
            onClick={() => onDelete(nummer)}
          >
            <i className='bi bi-trash me-1' />
            {t('delete')}
          </Button>
        )}
      </div>
    )
  }

  // Type 2: AML — has data.attributes
  if (isRecord(item.data)) {
    const data = item.data as AnyRecord
    const attrs = isRecord(data.attributes) ? data.attributes : {}
    const datasetsRaw = Array.isArray(item.datasets)
      ? item.datasets
      : Array.isArray(data.datasets)
        ? data.datasets
        : []
    const datasets = (datasetsRaw as unknown[]).filter((v): v is string => typeof v === 'string')

    return (
      <div className='mb-3 pb-2 border-bottom'>
        {datasets.length > 0 && (
          <div className='small mb-1'>
            <b>Datasets</b>: {datasets.join(', ')}
          </div>
        )}
        {renderNestedAttributes(attrs)}
      </div>
    )
  }

  // Type 3: Generic flat key-value (other services)
  const entries = Object.entries(item).filter(([tag, value]) => {
    if (SKIP_GENERIC.has(tag)) return false
    if (typeof value === 'string' && value.toUpperCase() === 'UNKNOWN') return false
    return value !== null && value !== undefined && value !== ''
  })

  if (entries.length === 0) return null

  return (
    <div className='mb-3 pb-2 border-bottom'>
      {entries.map(([tag, value]) => (
        <div key={tag} className='small'>
          <b>{tag}</b>:{' '}
          {Array.isArray(value)
            ? (value as unknown[]).join(', ')
            : typeof value === 'boolean'
              ? value
                ? 'Ja'
                : 'Nein'
              : typeof value === 'string' && (value === 'true' || value === 'false')
                ? boolStr(value)
                : String(value)}
        </div>
      ))}
    </div>
  )
}

function normalizeHits(value: unknown): AnyRecord[] {
  const result: AnyRecord[] = []
  const collect = (v: unknown) => {
    if (Array.isArray(v)) {
      v.forEach(collect)
      return
    }
    if (isRecord(v)) result.push(v)
  }
  collect(value)
  return result
}

function shouldShow(item: AnyRecord): boolean {
  if (item.deleted || item.invalid) return false
  // For SCHUFA hits, only show Status OK
  if ('Status' in item) return item.Status === 'OK'
  return true
}

function KycCkecks({ show, kyc, onClose, onDelete }: KycChecksProps) {
  const items = useMemo(() => normalizeHits(kyc).filter(shouldShow), [kyc])

  return (
    <Modal show={show} centered scrollable size='lg' onHide={onClose}>
      <Modal.Header className='bg-secondary text-white' closeButton>
        <Modal.Title>
          <i className='bi bi-shield-check me-2' />
          KYC Search
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {items.length === 0 ? (
          <p className='text-muted mb-0'>{t('noResults')}</p>
        ) : (
          <Table borderless size='sm' className='mb-0'>
            <tbody>
              {items.map((item, i) => (
                <tr key={i}>
                  <td>
                    <KycHit item={item} onDelete={onDelete} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          {t('close')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default KycCkecks
